import React from 'react';

/**
 * This context solves the problem of components above the Search component
 * needing to control the query value of the search input.
 *
 * PageHeader takes a searchComponent prop, which is a React node that
 * PageHeader renders. PageHeader is designed this way so that
 *
 * 1) The user of PageHeader can render any searchComponent
 * they want in the header. So if somewhere down the line we have a
 * page on the dashboard that for some reason doesn't want to use the
 * normal Search component, it can just pass in its own.
 *
 * 2) The props of PageHeader don't get bloated. So instead of
 * rendering PageHeader as
 *
 * <PageHeader
 *   ...all of the regular PageHeader props
 *   onSearch={...}
 *   searchDebounce={...}
 *   searchCount={...}
 *   searchPlaceholder={...}
 * />
 *
 * you can instead render it as
 *
 * <PageHeader
 *   ...all of the regular PageHeader props
 *   searchComponent={<Search ... />}
 * />
 *
 * Moreso #2 than #1, but overall it just provides more flexibility,
 * and keeps PageHeader from having too much responsibility.
 *
 * The problem is that PageHeader also includes a "Show All" button,
 * that when clicked is supposed to, among other things, clear the search
 * query. But since the Search component is defined outside of PageHeader,
 * PageHeader has no control over the Search component's query value.
 *
 * So to solve this, we require PageHeader to provide, and the searchComponent
 * passed to it to consume, this QueryContext. The searchComponent can use this context
 * to read and write the search query that appears in the input, while still calling
 * its own `onChange` prop with the debounced query. PageHeader can manage then state
 * that provides this context, allowing it to clear the value when the "Show All" button
 * is clicked.
 *
 * Alternative Solution:
 *
 * - Just let the creator of the searchComponent manage the state of the query,
 * and pass that state as props.
 *
 * This would mean
 *
 * 1. Each user of PageHeader has to manage its own search query state, which isn't
 * a great dev experience, and is also just annoying.
 *
 * and either
 *
 * 2a. Each user of PageHeader also has to manage the debouncing of the value, which
 * would be extremely annoying.
 *
 * or
 *
 * 2b. The searchComponent would need to accept and manage calling 2 different props,
 * one for the actual value of the input, and one for the debounced value. This would
 * be confusing, and, since the parent component already has access to the input value,
 * it might seem strange for the searchComponent to be handling debouncing at all with
 * this design.
 *
 */
export const QueryContext = React.createContext({
  query: '',
  setQuery: (_: string) => {},
});
